import AsyncStorage from '@react-native-async-storage/async-storage';
let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  TextInput,
  ActivityIndicator,
  Alert,
  Modal,
  RefreshControl,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import portfolioDatabase from '../../services/PortfolioDatabase';
import { apiFetch } from '../../utils/apiClient';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
export default function AllEducationScreen({ navigation }) {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // === Live API Data ===
  const [userId, setUserId] = React.useState('default');
  React.useEffect(() => {
    AsyncStorage.getItem('userToken').then(t => { if (t) setUserId(t.slice(0, 16)); }).catch(() => {});
  }, []);
  React.useEffect(() => {
    if (userId === 'default') return;
    const _ck = `professional_${_profUserId}_alleducationscreen`;
    apiFetch('/api/data/customers?limit=1').then(d => {
      if (d && !d.error) {
        const arr = Array.isArray(d) ? d : (d.data || d.results || []);
        AsyncStorage.setItem(_ck, JSON.stringify(arr)).catch(() => {});
      }
    }).catch(() => {});
  }, [userId]);

  const [education, setEducation] = useState([]);
  const [filteredEducation, setFilteredEducation] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [sortBy, setSortBy] = useState('startDate');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEducation, setEditingEducation] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    degree: '',
    institution: '',
    fieldOfStudy: '',
    level: 'Bachelor\'s',
    status: 'completed',
    startDate: '',
    endDate: '',
    location: '',
    gpa: '',
    description: '',
    achievements: [],
  });

  // Education levels
  const educationLevels = [
    'High School',
    'Associate',
    'Bachelor\'s',
    'Master\'s',
    'PhD',
    'Certificate',
    'Diploma',
    'Professional',
  ];

  // Status options
  const statusOptions = [
    'completed',
    'in_progress',
    'dropped_out',
  ];

  // Sort options
  const sortOptions = [
    { key: 'startDate', label: 'Start Date' },
    { key: 'institution', label: 'Institution' },
    { key: 'degree', label: 'Degree' },
    { key: 'level', label: 'Level' },
    { key: 'gpa', label: 'GPA' },
  ];

  // Load education data
  const loadEducation = async () => {
    try {
      console.log('🔄 Loading education data...');
      
      // Initialize database if not already done
      if (!portfolioDatabase.initialized) {
        await portfolioDatabase.initialize();
      }

      const educationData = await portfolioDatabase.getEducation();
      console.log('✅ Education data loaded:', educationData.length, 'entries');
      
      setEducation(educationData || []);
      setFilteredEducation(educationData || []);
    } catch (error) {
      console.error('❌ Error loading education:', error);
      Alert.alert('Error', 'Failed to load education data. Please try again.');
      setEducation([]);
      setFilteredEducation([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Load data on component mount and focus
  useFocusEffect(
    useCallback(() => {
      loadEducation();
    }, [])
  );

  // Filter and sort education
  useEffect(() => {
    let filtered = [...education];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(edu =>
        String(edu.degree ?? '').toLowerCase().includes(query) ||
        String(edu.institution ?? '').toLowerCase().includes(query) ||
        String(edu.fieldOfStudy ?? '').toLowerCase().includes(query) ||
        String(edu.location ?? '').toLowerCase().includes(query)
      );
    }

    // Apply level filter
    if (selectedLevel !== 'all') {
      filtered = filtered.filter(edu => edu.level === selectedLevel);
    }

    // Apply status filter
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(edu => edu.status === selectedStatus);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'startDate':
          return new Date(b.startDate) - new Date(a.startDate);
        case 'institution':
          return a.institution.localeCompare(b.institution);
        case 'degree':
          return a.degree.localeCompare(b.degree);
        case 'level':
          return a.level.localeCompare(b.level);
        case 'gpa':
          return (parseFloat(b.gpa) || 0) - (parseFloat(a.gpa) || 0);
        default:
          return 0;
      }
    });

    setFilteredEducation(filtered);
  }, [education, searchQuery, selectedLevel, selectedStatus, sortBy]);

  // Handle refresh
  const handleRefresh = () => {
    setRefreshing(true);
    loadEducation();
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      degree: '',
      institution: '',
      fieldOfStudy: '',
      level: 'Bachelor\'s',
      status: 'completed',
      startDate: '',
      endDate: '',
      location: '',
      gpa: '',
      description: '',
      achievements: [],
    });
  };

  // Handle add education
  const handleAddEducation = async () => {
    try {
      if (!formData.degree.trim() || !formData.institution.trim()) {
        Alert.alert('Error', 'Please fill in degree and institution fields.');
        return;
      }

      const educationData = {
        ...formData,
        degree: formData.degree.trim(),
        institution: formData.institution.trim(),
        fieldOfStudy: formData.fieldOfStudy.trim(),
        location: formData.location.trim(),
        description: formData.description.trim(),
      };

      await portfolioDatabase.addEducation(educationData);
      
      setShowAddModal(false);
      resetForm();
      loadEducation();
      
      Alert.alert('Success', 'Education added successfully!');
    } catch (error) {
      console.error('❌ Error adding education:', error);
      Alert.alert('Error', 'Failed to add education. Please try again.');
    }
  };

  // Handle edit education
  const handleEditEducation = async () => {
    try {
      if (!formData.degree.trim() || !formData.institution.trim()) {
        Alert.alert('Error', 'Please fill in degree and institution fields.');
        return;
      }

      const updates = {
        ...formData,
        degree: formData.degree.trim(),
        institution: formData.institution.trim(),
        fieldOfStudy: formData.fieldOfStudy.trim(),
        location: formData.location.trim(),
        description: formData.description.trim(),
      };

      await portfolioDatabase.updateEducation(editingEducation.id, updates);
      
      setShowEditModal(false);
      setEditingEducation(null);
      resetForm();
      loadEducation();
      
      Alert.alert('Success', 'Education updated successfully!');
    } catch (error) {
      console.error('❌ Error updating education:', error);
      Alert.alert('Error', 'Failed to update education. Please try again.');
    }
  };

  // Handle delete education
  const handleDeleteEducation = (educationItem) => {
    Alert.alert(
      'Delete Education',
      `Are you sure you want to delete "${educationItem.degree}" from ${educationItem.institution}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              await portfolioDatabase.deleteEducation(educationItem.id);
              loadEducation();
              Alert.alert('Success', 'Education deleted successfully!');
            } catch (error) {
              console.error('❌ Error deleting education:', error);
              Alert.alert('Error', 'Failed to delete education. Please try again.');
            }
          },
        },
      ]
    );
  };

  // Open edit modal
  const openEditModal = (educationItem) => {
    setFormData({
      degree: educationItem.degree || '',
      institution: educationItem.institution || '',
      fieldOfStudy: educationItem.fieldOfStudy || '',
      level: educationItem.level || 'Bachelor\'s',
      status: educationItem.status || 'completed',
      startDate: educationItem.startDate || '',
      endDate: educationItem.endDate || '',
      location: educationItem.location || '',
      gpa: educationItem.gpa || '',
      description: educationItem.description || '',
      achievements: educationItem.achievements || [],
    });
    setEditingEducation(educationItem);
    setShowEditModal(true);
  };

  // Get level icon
  const getLevelIcon = (level) => {
    switch (level) {
      case 'High School':
        return 'school-outline';
      case 'Associate':
        return 'library-outline';
      case 'Bachelor\'s':
        return 'school';
      case 'Master\'s':
        return 'medal-outline';
      case 'PhD':
        return 'trophy-outline';
      case 'Certificate':
        return 'ribbon-outline';
      case 'Diploma':
        return 'document-text-outline';
      case 'Professional':
        return 'briefcase-outline';
      default:
        return 'school-outline';
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return '#10B981';
      case 'in_progress':
        return '#3B82F6';
      case 'dropped_out':
        return '#EF4444';
      default:
        return '#64748B';
    }
  };

  // Get status label
  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'in_progress':
        return 'In Progress';
      case 'dropped_out':
        return 'Dropped Out';
      default:
        return 'Unknown';
    }
  };

  // Render education item
  const renderEducationItem = (educationItem) => (
    <View key={educationItem.id} style={styles.educationCard}>
      <View style={styles.educationHeader}>
        <View style={styles.educationLeft}>
          <View style={styles.levelIconContainer}>
            <Ionicons
              name={getLevelIcon(educationItem.level)}
              size={24}
              color="#0e4e96"
            />
          </View>
          <View style={styles.educationInfo}>
            <Text style={styles.educationDegree}>{educationItem.degree}</Text>
            <Text style={styles.educationInstitution}>{educationItem.institution}</Text>
            {educationItem.fieldOfStudy && (
              <Text style={styles.educationField}>{educationItem.fieldOfStudy}</Text>
            )}
            {educationItem.location && (
              <Text style={styles.educationLocation}>{educationItem.location}</Text>
            )}
          </View>
        </View>
        <View style={styles.educationRight}>
          <View style={[styles.statusBadge, { backgroundColor: getStatusColor(educationItem.status) + '20' }]}>
            <Text style={[styles.statusText, { color: getStatusColor(educationItem.status) }]}>
              {getStatusLabel(educationItem.status)}
            </Text>
          </View>
          <Text style={styles.educationLevel}>{educationItem.level}</Text>
          {educationItem.startDate && educationItem.endDate && (
            <Text style={styles.educationDates}>
              {educationItem.startDate} - {educationItem.endDate}
            </Text>
          )}
          {educationItem.gpa && (
            <Text style={styles.educationGPA}>GPA: {educationItem.gpa}</Text>
          )}
        </View>
      </View>

      {educationItem.description && (
        <Text style={styles.educationDescription}>{educationItem.description}</Text>
      )}

      {educationItem.achievements && educationItem.achievements.length > 0 && (
        <View style={styles.achievementsContainer}>
          <Text style={styles.achievementsTitle}>Achievements:</Text>
          {educationItem.achievements.map((achievement, index) => (
            <Text key={index} style={styles.achievementItem}>• {achievement}</Text>
          ))}
        </View>
      )}

      <View style={styles.educationActions}>
        <TouchableOpacity
          style={styles.actionButton}
          onPress={() => openEditModal(educationItem)}
        >
          <Ionicons name="create-outline" size={20} color="#0e4e96" />
          <Text style={styles.actionButtonText}>Edit</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.actionButton, styles.deleteButton]}
          onPress={() => handleDeleteEducation(educationItem)}
        >
          <Ionicons name="trash-outline" size={20} color="#EF4444" />
          <Text style={[styles.actionButtonText, styles.deleteButtonText]}>Delete</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  // Render form modal
  const renderFormModal = (isEdit = false) => (
    <Modal
      visible={isEdit ? showEditModal : showAddModal}
      transparent={true}
      animationType="slide"
      onRequestClose={() => {
        if (isEdit) {
          setShowEditModal(false);
          setEditingEducation(null);
        } else {
          setShowAddModal(false);
        }
        resetForm();
      }}
    >
      <View style={styles.modalContainer}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>
              {isEdit ? 'Edit Education' : 'Add Education'}
            </Text>
            <TouchableOpacity
              style={styles.modalCloseButton}
              onPress={() => {
                if (isEdit) {
                  setShowEditModal(false);
                  setEditingEducation(null);
                } else {
                  setShowAddModal(false);
                }
                resetForm();
              }}
            >
              <Ionicons name="close" size={24} color="#64748B" />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.modalBody} showsVerticalScrollIndicator={false}>
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Degree *</Text>
              <TextInput
                style={styles.formInput}
                value={formData.degree}
                onChangeText={(text) => setFormData({ ...formData, degree: text })}
                placeholder="e.g., Bachelor of Computer Science"
                placeholderTextColor="#94A3B8"
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Institution *</Text>
              <TextInput
                style={styles.formInput}
                value={formData.institution}
                onChangeText={(text) => setFormData({ ...formData, institution: text })}
                placeholder="University/School name"
                placeholderTextColor="#94A3B8"
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Field of Study</Text>
              <TextInput
                style={styles.formInput}
                value={formData.fieldOfStudy}
                onChangeText={(text) => setFormData({ ...formData, fieldOfStudy: text })}
                placeholder="e.g., Computer Science"
                placeholderTextColor="#94A3B8"
              />
            </View>

            <View style={styles.formRow}>
              <View style={styles.formGroupHalf}>
                <Text style={styles.formLabel}>Education Level</Text>
                <View style={styles.pickerContainer}>
                  <TouchableOpacity
                    style={styles.pickerButton}
                    onPress={() => {
                      Alert.alert(
                        'Select Education Level',
                        '',
                        educationLevels.map(level => ({
                          text: level,
                          onPress: () => setFormData({ ...formData, level }),
                        }))
                      );
                    }}
                  >
                    <Text style={styles.pickerText}>{formData.level}</Text>
                    <Ionicons name="chevron-down" size={20} color="#64748B" />
                  </TouchableOpacity>
                </View>
              </View>

              <View style={styles.formGroupHalf}>
                <Text style={styles.formLabel}>Status</Text>
                <View style={styles.pickerContainer}>
                  <TouchableOpacity
                    style={styles.pickerButton}
                    onPress={() => {
                      Alert.alert(
                        'Select Status',
                        '',
                        statusOptions.map(status => ({
                          text: getStatusLabel(status),
                          onPress: () => setFormData({ ...formData, status }),
                        }))
                      );
                    }}
                  >
                    <Text style={styles.pickerText}>{getStatusLabel(formData.status)}</Text>
                    <Ionicons name="chevron-down" size={20} color="#64748B" />
                  </TouchableOpacity>
                </View>
              </View>
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Location</Text>
              <TextInput
                style={styles.formInput}
                value={formData.location}
                onChangeText={(text) => setFormData({ ...formData, location: text })}
                placeholder="City, Country"
                placeholderTextColor="#94A3B8"
              />
            </View>

            <View style={styles.formRow}>
              <View style={styles.formGroupHalf}>
                <Text style={styles.formLabel}>Start Date</Text>
                <TextInput
                  style={styles.formInput}
                  value={formData.startDate}
                  onChangeText={(text) => setFormData({ ...formData, startDate: text })}
                  placeholder="MM/YYYY"
                  placeholderTextColor="#94A3B8"
                />
              </View>

              <View style={styles.formGroupHalf}>
                <Text style={styles.formLabel}>End Date</Text>
                <TextInput
                  style={styles.formInput}
                  value={formData.endDate}
                  onChangeText={(text) => setFormData({ ...formData, endDate: text })}
                  placeholder="MM/YYYY or Present"
                  placeholderTextColor="#94A3B8"
                />
              </View>
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>GPA (Optional)</Text>
              <TextInput
                style={styles.formInput}
                value={formData.gpa}
                onChangeText={(text) => setFormData({ ...formData, gpa: text })}
                placeholder="e.g., 3.8"
                placeholderTextColor="#94A3B8"
                keyboardType="decimal-pad"
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Description</Text>
              <TextInput
                style={[styles.formInput, styles.textArea]}
                value={formData.description}
                onChangeText={(text) => setFormData({ ...formData, description: text })}
                placeholder="Relevant coursework, achievements, honors, etc."
                placeholderTextColor="#94A3B8"
                multiline
                numberOfLines={4}
                textAlignVertical="top"
              />
            </View>
          </ScrollView>

          <View style={styles.modalFooter}>
            <TouchableOpacity
              style={[styles.modalButton, styles.cancelButton]}
              onPress={() => {
                if (isEdit) {
                  setShowEditModal(false);
                  setEditingEducation(null);
                } else {
                  setShowAddModal(false);
                }
                resetForm();
              }}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.modalButton, styles.saveButton]}
              onPress={isEdit ? handleEditEducation : handleAddEducation}
            >
              <Text style={styles.saveButtonText}>
                {isEdit ? 'Update' : 'Add'} Education
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  // Render filter modal
  const renderFilterModal = () => (
    <Modal
      visible={showFilterModal}
      transparent={true}
      animationType="slide"
      onRequestClose={() => setShowFilterModal(false)}
    >
      <View style={styles.modalContainer}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Filter & Sort</Text>
            <TouchableOpacity
              style={styles.modalCloseButton}
              onPress={() => setShowFilterModal(false)}
            >
              <Ionicons name="close" size={24} color="#64748B" />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.modalBody}>
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Education Level</Text>
              <View style={styles.filterOptions}>
                <TouchableOpacity
                  style={[
                    styles.filterOption,
                    selectedLevel === 'all' && styles.filterOptionActive,
                  ]}
                  onPress={() => setSelectedLevel('all')}
                >
                  <Text
                    style={[
                      styles.filterOptionText,
                      selectedLevel === 'all' && styles.filterOptionTextActive,
                    ]}
                  >
                    All Levels
                  </Text>
                </TouchableOpacity>
                {educationLevels.map((level) => (
                  <TouchableOpacity
                    key={level}
                    style={[
                      styles.filterOption,
                      selectedLevel === level && styles.filterOptionActive,
                    ]}
                    onPress={() => setSelectedLevel(level)}
                  >
                    <Text
                      style={[
                        styles.filterOptionText,
                        selectedLevel === level && styles.filterOptionTextActive,
                      ]}
                    >
                      {level}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Status</Text>
              <View style={styles.filterOptions}>
                <TouchableOpacity
                  style={[
                    styles.filterOption,
                    selectedStatus === 'all' && styles.filterOptionActive,
                  ]}
                  onPress={() => setSelectedStatus('all')}
                >
                  <Text
                    style={[
                      styles.filterOptionText,
                      selectedStatus === 'all' && styles.filterOptionTextActive,
                    ]}
                  >
                    All Status
                  </Text>
                </TouchableOpacity>
                {statusOptions.map((status) => (
                  <TouchableOpacity
                    key={status}
                    style={[
                      styles.filterOption,
                      selectedStatus === status && styles.filterOptionActive,
                    ]}
                    onPress={() => setSelectedStatus(status)}
                  >
                    <Text
                      style={[
                        styles.filterOptionText,
                        selectedStatus === status && styles.filterOptionTextActive,
                      ]}
                    >
                      {getStatusLabel(status)}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Sort By</Text>
              <View style={styles.filterOptions}>
                {sortOptions.map((option) => (
                  <TouchableOpacity
                    key={option.key}
                    style={[
                      styles.filterOption,
                      sortBy === option.key && styles.filterOptionActive,
                    ]}
                    onPress={() => setSortBy(option.key)}
                  >
                    <Text
                      style={[
                        styles.filterOptionText,
                        sortBy === option.key && styles.filterOptionTextActive,
                      ]}
                    >
                      {option.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </ScrollView>

          <View style={styles.modalFooter}>
            <TouchableOpacity
              style={[styles.modalButton, styles.resetButton]}
              onPress={() => {
                setSelectedLevel('all');
                setSelectedStatus('all');
                setSortBy('startDate');
                setSearchQuery('');
              }}
            >
              <Text style={styles.resetButtonText}>Reset</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.modalButton, styles.applyButton]}
              onPress={() => setShowFilterModal(false)}
            >
              <Text style={styles.applyButtonText}>Apply</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading education...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="#ffffff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Education</Text>
        <TouchableOpacity
          style={styles.addButton}
          onPress={() => setShowAddModal(true)}
        >
          <Ionicons name="add" size={24} color="#ffffff" />
        </TouchableOpacity>
      </View>

      {/* Search and Filter */}
      <View style={styles.searchContainer}>
        <View style={styles.searchInputContainer}>
          <Ionicons name="search" size={20} color="#64748B" />
          <TextInput
            style={styles.searchInput}
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholder="Search education..."
            placeholderTextColor="#94A3B8"
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <Ionicons name="close-circle" size={20} color="#64748B" />
            </TouchableOpacity>
          )}
        </View>
        <TouchableOpacity
          style={styles.filterButton}
          onPress={() => setShowFilterModal(true)}
        >
          <Ionicons name="options-outline" size={20} color="#0e4e96" />
        </TouchableOpacity>
      </View>

      {/* Active Filters */}
      {(selectedLevel !== 'all' || selectedStatus !== 'all' || searchQuery.trim()) && (
        <View style={styles.activeFilters}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            {selectedLevel !== 'all' && (
              <View style={styles.activeFilter}>
                <Text style={styles.activeFilterText}>Level: {selectedLevel}</Text>
                <TouchableOpacity onPress={() => setSelectedLevel('all')}>
                  <Ionicons name="close" size={16} color="#0e4e96" />
                </TouchableOpacity>
              </View>
            )}
            {selectedStatus !== 'all' && (
              <View style={styles.activeFilter}>
                <Text style={styles.activeFilterText}>Status: {getStatusLabel(selectedStatus)}</Text>
                <TouchableOpacity onPress={() => setSelectedStatus('all')}>
                  <Ionicons name="close" size={16} color="#0e4e96" />
                </TouchableOpacity>
              </View>
            )}
            {searchQuery.trim() && (
              <View style={styles.activeFilter}>
                <Text style={styles.activeFilterText}>Search: {searchQuery}</Text>
                <TouchableOpacity onPress={() => setSearchQuery('')}>
                  <Ionicons name="close" size={16} color="#0e4e96" />
                </TouchableOpacity>
              </View>
            )}
          </ScrollView>
        </View>
      )}

      {/* Education List */}
      <ScrollView
        style={styles.container}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
        }
      >
        {filteredEducation.length > 0 ? (
          <View style={styles.educationList}>
            {filteredEducation.map(renderEducationItem)}
          </View>
        ) : (
          <View style={styles.emptyContainer}>
            <Ionicons name="school-outline" size={64} color="#94A3B8" />
            <Text style={styles.emptyTitle}>
              {education.length === 0 ? 'No Education Added' : 'No Results Found'}
            </Text>
            <Text style={styles.emptyDescription}>
              {education.length === 0
                ? 'Add your educational background to showcase your qualifications.'
                : 'Try adjusting your search or filter criteria.'}
            </Text>
            {education.length === 0 && (
              <TouchableOpacity
                style={styles.emptyButton}
                onPress={() => setShowAddModal(true)}
              >
                <Text style={styles.emptyButtonText}>Add Education</Text>
              </TouchableOpacity>
            )}
          </View>
        )}
      </ScrollView>

      {/* Modals */}
      {renderFormModal(false)}
      {renderFormModal(true)}
      {renderFilterModal()}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0e4e96',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#0e4e96',
  },
  backButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#ffffff',
    textAlign: 'center',
  },
  addButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
  },
  loadingText: {
    fontSize: 16,
    color: '#64748B',
    marginTop: 16,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
    gap: 12,
  },
  searchInputContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F1F5F9',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 12,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#0F172A',
  },
  filterButton: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    justifyContent: 'center',
  },
  activeFilters: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  activeFilter: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#0e4e96' + '20',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    marginRight: 8,
    gap: 6,
  },
  activeFilterText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#0e4e96',
  },
  educationList: {
    padding: 20,
    gap: 16,
  },
  educationCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  educationHeader: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  educationLeft: {
    flex: 1,
    flexDirection: 'row',
    gap: 12,
  },
  levelIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#0e4e96' + '20',
    alignItems: 'center',
    justifyContent: 'center',
  },
  educationInfo: {
    flex: 1,
  },
  educationDegree: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 4,
  },
  educationInstitution: {
    fontSize: 14,
    fontWeight: '500',
    color: '#0e4e96',
    marginBottom: 2,
  },
  educationField: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 2,
  },
  educationLocation: {
    fontSize: 12,
    color: '#94A3B8',
  },
  educationRight: {
    alignItems: 'flex-end',
    gap: 4,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
  },
  educationLevel: {
    fontSize: 12,
    fontWeight: '500',
    color: '#64748B',
  },
  educationDates: {
    fontSize: 12,
    color: '#94A3B8',
    fontStyle: 'italic',
  },
  educationGPA: {
    fontSize: 12,
    fontWeight: '600',
    color: '#10B981',
  },
  educationDescription: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
    marginBottom: 12,
  },
  achievementsContainer: {
    marginBottom: 16,
  },
  achievementsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 8,
  },
  achievementItem: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
    marginBottom: 4,
  },
  educationActions: {
    flexDirection: 'row',
    gap: 12,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#0e4e96',
    gap: 6,
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0e4e96',
  },
  deleteButton: {
    borderColor: '#EF4444',
  },
  deleteButtonText: {
    color: '#EF4444',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
    paddingVertical: 60,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#0F172A',
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  emptyDescription: {
    fontSize: 16,
    color: '#64748B',
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 32,
  },
  emptyButton: {
    backgroundColor: '#0e4e96',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  emptyButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '90%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
  },
  modalCloseButton: {
    padding: 4,
  },
  modalBody: {
    padding: 20,
    maxHeight: 400,
  },
  formGroup: {
    marginBottom: 20,
  },
  formRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 20,
  },
  formGroupHalf: {
    flex: 1,
  },
  formLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 8,
  },
  formInput: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    color: '#0F172A',
    backgroundColor: '#FFFFFF',
  },
  textArea: {
    minHeight: 100,
    textAlignVertical: 'top',
  },
  pickerContainer: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
  },
  pickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  pickerText: {
    fontSize: 16,
    color: '#0F172A',
  },
  modalFooter: {
    flexDirection: 'row',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#E2E8F0',
    gap: 12,
  },
  modalButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelButton: {
    backgroundColor: '#F1F5F9',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#64748B',
  },
  saveButton: {
    backgroundColor: '#0e4e96',
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  resetButton: {
    backgroundColor: '#F1F5F9',
  },
  resetButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#64748B',
  },
  applyButton: {
    backgroundColor: '#0e4e96',
  },
  applyButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  filterSection: {
    marginBottom: 24,
  },
  filterSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 12,
  },
  filterOptions: {
    gap: 8,
  },
  filterOption: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    backgroundColor: '#FFFFFF',
  },
  filterOptionActive: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  filterOptionText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
  },
  filterOptionTextActive: {
    color: '#FFFFFF',
  },
});
